// Unicode error icon for simple strings
export const ERR = "✖ ";
export const err = (s: string): string => ERR + " " + s;

// Deeply copy an object by serializing to JSON, then deserializing
export function deepCopy<T>(o: T): T {
  return JSON.parse(JSON.stringify(o));
}

// Delay for async/await
export const delayMs = (timeMs: number) => {
  return new Promise((resolve) => setTimeout(resolve, timeMs));
};

// Assertion for type-checked matching of all variants in sum types, e.g.
// put it in the unreachable else branch when matching a sum type using if-else chain.
export const assertUnreachable = (_x: never): never => {
  throw new Error("Unhandled variant");
};

// Type guard useful to e.g. type-safe list filtering `.filter(isDefined)`.
export function isDefined<T>(val: T | undefined): val is T {
  return val != undefined;
}

// Type level magic to ensure same types in a union and an array: https://stackoverflow.com/a/69542981
// eslint-disable-next-line
type NoneEmptyArray = readonly any[] & { 0: any };
type CompareUnionWithArray<P, Q extends NoneEmptyArray> = Exclude<
  P,
  Q[number]
> extends never
  ? Exclude<Q[number], P> extends never
    ? Q
    : ReadonlyArray<P>
  : readonly [...Q, Exclude<P, Q[number]>];
/* eslint-disable @typescript-eslint/no-empty-function */
export function assertUnionArrayEquals<P, Q extends NoneEmptyArray>(
  _test: CompareUnionWithArray<P, Q>
): void {}
// const _doAssertTypeEquals = () =>
//   assertUnionArrayEquals<MyUnion, typeof MyConstArray>(MyConstArray);

export class PairSet {
  entries: Record<number, Record<number, boolean>> = {};
  count = 0;

  add(x: number, y: number): void {
    if (!this.has(x, y)) this.count++;
    this.entries[x] ||= {};
    this.entries[x][y] = true;
  }

  remove(x: number, y: number): void {
    if (!this.has(x, y)) return;
    this.count--;
    delete this.entries[x][y];
    if (Object.keys(this.entries[x]).length == 0) delete this.entries[x];
  }

  toggle(x: number, y: number): void {
    if (this.has(x, y)) this.remove(x, y);
    else this.add(x, y);
  }

  has(x: number, y: number): boolean {
    return !!this.entries[x]?.[y];
  }

  iter(): [number, number][] {
    const list: [number, number][] = [];
    for (const [x, inner] of Object.entries(this.entries)) {
      for (const [y, v] of Object.entries(inner)) {
        if (v != true) throw Error(`Set value not true at (${x}, ${y}) = ${v}`);
        list.push([Number(x), Number(y)]);
      }
    }
    return list;
  }
}

export const decToHex = (num: number, width?: number) =>
  (+num).toString(16).padStart(width ?? 0, "0");

export function delayedCall(timeout_ms: number, fn: () => void): () => void {
  let timerId = null as null | number;
  return () => {
    if (timerId != null) clearTimeout(timerId);
    timerId = setTimeout(fn, timeout_ms);
  };
}

export const webUsbSupported = () => "usb" in navigator;

export const fs = {
  baseName: (path: string, sep?: string | RegExp): string => {
    sep = sep ?? /[/\\]/;
    const parts = path.split(sep);
    return parts[parts.length - 1];
  },

  splitExt: (path: string): [string, string] => {
    const dot = path.lastIndexOf(".");
    return dot < 0 ? [path, ""] : [path.substring(0, dot), path.substring(dot)];
  },
};
